// src/pages/user/Settings.js
// Page paramètres - géolocalisation activée par défaut, persiste après actualisation - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/shared/LanguageSwitcher';
import LocationPermissionModal from '../../components/shared/LocationPermissionModal';
import { useTheme } from '../../hooks/useTheme';
import { getGeoPreference, setGeoPreference } from '../../utils/geoPreference';

const Settings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  // Préférence locale (fiable, persiste) - source de vérité principale pour l'affichage du bouton
  const [geoEnabled, setGeoEnabled] = useState(true);
  // État réel du navigateur (informatif seulement, peu fiable sur certains appareils comme Safari iOS)
  const [browserDenied, setBrowserDenied] = useState(false);
  const [notifStatus, setNotifStatus] = useState('checking');
  const [showGeoGuide, setShowGeoGuide] = useState(false);

  useEffect(() => {
    // Charge la préférence sauvegardée (activée par défaut si jamais définie)
    setGeoEnabled(getGeoPreference());

    // Vérifie seulement si le navigateur a explicitement BLOQUÉ (cas où on doit vraiment avertir)
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((result) => {
          setBrowserDenied(result.state === 'denied');
          result.onchange = () => setBrowserDenied(result.state === 'denied');
        })
        .catch(() => {});
    }

    if ('Notification' in window) {
      setNotifStatus(Notification.permission);
    } else {
      setNotifStatus('unsupported');
    }
  }, []);

  const handleGeoToggle = () => {
    if (browserDenied) {
      setShowGeoGuide(true);
      return;
    }

    if (geoEnabled) {
      // Désactivation : on respecte ton choix, ne redemandera plus tant que tu ne réactives pas
      setGeoEnabled(false);
      setGeoPreference(false);
    } else {
      // Réactivation : on redemande la vraie autorisation système si besoin
      setGeoEnabled(true);
      setGeoPreference(true);
      navigator.geolocation.getCurrentPosition(
        () => {},
        (error) => {
          if (error.code === 1) setBrowserDenied(true);
        },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  };

  const handleNotifToggle = async () => {
    if (notifStatus === 'granted' || notifStatus === 'unsupported') return;
    if (notifStatus === 'denied') {
      alert("Les notifications sont bloquées au niveau de votre navigateur. Réactivez-les depuis les réglages du site.");
      return;
    }
    const result = await Notification.requestPermission();
    setNotifStatus(result);
  };

  const geoStatusLabel = browserDenied ? 'Bloqué' : geoEnabled ? 'Activé' : 'Désactivé';
  const geoStatusColor = browserDenied ? '#F56565' : geoEnabled ? '#48BB78' : theme.textMuted;

  const notifLabels = { granted: 'Activées', denied: 'Bloquées', default: 'Non activées', unsupported: 'Non disponibles', checking: '...' };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', position: 'sticky', top: 0, backgroundColor: theme.bg, zIndex: 50, borderBottom: `1px solid ${theme.borderSoft}` },
    backButton: { background: 'none', border: 'none', color: theme.textPrimary, fontSize: '20px', cursor: 'pointer', padding: '4px' },
    title: { fontSize: '20px', fontWeight: '700', color: theme.textPrimary },
    section: { padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
    sectionTitle: { fontSize: '13px', fontWeight: '600', color: theme.textMuted, textTransform: 'uppercase', padding: '12px 0 4px' },
    row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', backgroundColor: theme.bgCard, borderRadius: '10px', border: `1px solid ${theme.borderSoft}` },
    rowLabel: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '15px', color: theme.textPrimary },
    rowMeta: { display: 'flex', alignItems: 'center', gap: '8px' },
    statusText: (color) => ({ fontSize: '12px', fontWeight: '600', color }),
    toggle: (active) => ({
      width: '48px', height: '28px', borderRadius: '14px', cursor: 'pointer', position: 'relative',
      transition: 'background-color 200ms', backgroundColor: active ? theme.gold : theme.textMuted,
    }),
    toggleKnob: (isOn) => ({
      position: 'absolute', top: '2px', left: isOn ? '22px' : '2px', width: '24px', height: '24px',
      borderRadius: '50%', backgroundColor: '#FFFFFF', transition: 'left 200ms',
    }),
    hintBox: { padding: '10px 14px', backgroundColor: theme.bgInput, borderRadius: '8px', fontSize: '12px', color: theme.textSecondary, lineHeight: '1.4' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Paramètres | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <div style={styles.title}>⚙️ {t('common.settings')}</div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Général</div>
        <div style={styles.row}>
          <span style={styles.rowLabel}>🌍 {t('common.language')}</span>
          <LanguageSwitcher compact />
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Permissions de l'appareil</div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>📍 Géolocalisation</span>
          <div style={styles.rowMeta}>
            <span style={styles.statusText(geoStatusColor)}>{geoStatusLabel}</span>
            <div style={styles.toggle(geoEnabled && !browserDenied)} onClick={handleGeoToggle}>
              <div style={styles.toggleKnob(geoEnabled && !browserDenied)} />
            </div>
          </div>
        </div>

        <div style={styles.row}>
          <span style={styles.rowLabel}>🔔 Notifications</span>
          <div style={styles.rowMeta}>
            <span style={styles.statusText(notifStatus === 'granted' ? '#48BB78' : notifStatus === 'denied' ? '#F56565' : theme.textMuted)}>
              {notifLabels[notifStatus]}
            </span>
            <div style={styles.toggle(notifStatus === 'granted')} onClick={handleNotifToggle}>
              <div style={styles.toggleKnob(notifStatus === 'granted')} />
            </div>
          </div>
        </div>

        <div style={styles.hintBox}>
          La géolocalisation est activée par défaut pour vous montrer les annonces proches de vous.
          Vous pouvez la désactiver à tout moment en un clic.
        </div>
      </div>

      <LocationPermissionModal open={showGeoGuide} onClose={() => setShowGeoGuide(false)} />
    </div>
  );
};

export default Settings;